import { Languages } from '../LanguageContext';
import { FormItem, isPageBreak, Question, QuestionType } from '../types';
import { getValidationMessage } from './validationMessages';

/** Manual volunteer-name confirmation question (PSIFAS-12817). */
export const VOLUNTEER_NAME_QUESTION_KEY = 'DONORNAME';

export function isVolunteerNameQuestionKey(questionKey: string): boolean {
  return questionKey === VOLUNTEER_NAME_QUESTION_KEY;
}

/**
 * Labels used in questionnaire headers after {c_Name} is replaced.
 * Whitespace around the label and colon is optional so both
 * "366109684שם המטופל:Test Donor" and "366109684 שם המטופל: Test Donor" work.
 * Take the last match so "ID: … Name: …" / "ת.ז: … שם: …" keep the name, not the ID.
 */
const NAME_LABEL_PATTERN =
  /(?:^|[^\p{L}_])(?:שם(?:\s*המטופל)?|(?:Patient\s*)?Name|Имя(?:\s*пациента)?|اسم(?:\s*المريض)?|الاسم)\s*[:：]?\s*/giu;

/**
 * Insert a space when a patient ID was concatenated onto the name label,
 * e.g. "366109684שם המטופל: Test Donor" → "366109684 שם המטופל: Test Donor".
 * Also puts a space after the label colon when the name was glued on.
 */
export function separatePatientIdFromNameLabel(text: string): string {
  return text
    .replace(/(\d)((?:<\/[^>]+>)*)(שם)/gu, '$1$2 $3')
    .replace(/(\d)((?:<\/[^>]+>)*)((?:Patient\s*)?Name\b)/gi, '$1$2 $3')
    .replace(/(שם(?:\s*המטופל)?\s*[:：])(?=\S)/gu, '$1 ')
    .replace(/((?:Patient\s*)?Name\s*[:：])(?=\S)/gi, '$1 ');
}

export function toPlainQuestionText(html: string): string {
  return separatePatientIdFromNameLabel(
    html
      .replace(/&nbsp;/gi, ' ')
      .replace(/&#160;/gi, ' ')
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/\s+/g, ' ')
      .trim(),
  );
}

export function normalizeVolunteerName(name: string): string {
  return name
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

export function volunteerNamesMatch(typed: string, embedded: string): boolean {
  return normalizeVolunteerName(typed) === normalizeVolunteerName(embedded);
}

function isUnreplacedPlaceholder(name: string): boolean {
  return !name || name.includes('{c_Name}');
}

function extractNameFromPlainText(plain: string): string | null {
  const pattern = new RegExp(NAME_LABEL_PATTERN.source, NAME_LABEL_PATTERN.flags);
  let lastValid: string | null = null;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(plain)) !== null) {
    const name = plain.slice(match.index + match[0].length).trim();
    if (isUnreplacedPlaceholder(name)) {
      continue;
    }
    lastValid = name;
  }
  return lastValid;
}

function extractNameFromQuestion(question: Question): string | null {
  return extractNameFromPlainText(toPlainQuestionText(question.text));
}

function isSkippableQuestion(item: FormItem): boolean {
  return isPageBreak(item) || isVolunteerNameQuestionKey(item.question_key);
}

/**
 * Volunteer name shown at the top of the form, not DONORNAME.
 * Prefer HEADER identity lines so a later "Name:" prompt cannot win.
 * If no header has a parseable name (many forms put {c_Name} on a text
 * question), fall back to the first matching non-DONORNAME question.
 */
export function extractEmbeddedVolunteerName(questions: FormItem[]): string | null {
  const candidates = questions.filter((item): item is Question => !isSkippableQuestion(item));
  for (const item of candidates) {
    if (item.question_type !== QuestionType.HEADER) continue;
    const extracted = extractNameFromQuestion(item);
    if (extracted) return extracted;
  }
  for (const item of candidates) {
    if (item.question_type === QuestionType.HEADER) continue;
    const extracted = extractNameFromQuestion(item);
    if (extracted) return extracted;
  }
  return null;
}

/** Inline / submit error for DONORNAME vs the name at the top of the form. Empty when the check does not apply. */
export function getVolunteerNameMismatchError(
  allQuestions: FormItem[],
  typedName: string,
  language: Languages,
): string {
  const typed = typedName.trim();
  if (!typed) {
    return '';
  }
  const embedded = extractEmbeddedVolunteerName(allQuestions);
  if (!embedded) {
    return '';
  }
  if (volunteerNamesMatch(typed, embedded)) {
    return '';
  }
  return getValidationMessage(language, 'volunteer_name_mismatch', {
    name: embedded,
  });
}
